import { DeleteOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import { useAuth } from '../../contexts/Auth'
import { BottomNav, ProgressHeader, RequiredMark, TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

export interface Step4Data {
  highlights: string[]
  description: string
  instantBooking: boolean
  freeWaitingTime: string
  overtimeFee: string
  extraBabySeatFee: string
  cancellationPolicy: string
  advancedBookingTime: string
  included: string[]
  notIncluded: string[]
}

export const defaultStep4Data: Step4Data = {
  highlights: [''],
  description: '',
  instantBooking: false,
  freeWaitingTime: '2 hours',
  overtimeFee: '0',
  extraBabySeatFee: '',
  cancellationPolicy: '48 hours before the trip',
  advancedBookingTime: '1 day',
  included: [''],
  notIncluded: [''],
}

interface Props {
  data: Step4Data
  onDataChange: (d: Step4Data) => void
  currentStep: number
  totalSteps: number
  maxStep?: number
  draftId: number | null
  onExit: () => void
  onBack: () => void
  onNext: (data: any, shouldNavigate?: boolean) => Promise<void>
  onPublish: () => Promise<void>
  onFinish: () => void
}

type View = 'service-info' | 'policies' | 'complete' | 'loading' | 'success'

const FREE_WAITING_OPTIONS = ['30 minutes', '1 hour', '1.5 hours', '2 hours', '3 hours']
const CANCELLATION_OPTIONS = [
  'Same day',
  '24 hours before the trip',
  '48 hours before the trip',
  '72 hours before the trip',
  '7 days before the trip',
]
const ADVANCED_BOOKING_OPTIONS = ['Same day', '1 day', '2 days', '3 days', '7 days']

const SUCCESS_ICON =
  'https://ts-upload.sgp1.digitaloceanspaces.com/cms/54Ro23B7yrHtewPQHW86j3hfrZdzByFzAyAPHewz97DwXHHAaYLUAGVZepj6FPW3QP1WeowsxhgqE5LWNhR3phBN.png'
const UPLOAD_ICON =
  'https://staging-ts-upload.sgp1.digitaloceanspaces.com/cms/3ttUJz5p2yQrzMbhYh6PhagekngjHawpm3EU1XpzBVthubysQX1fDYv6TBWsXVbd96yfrwPFcmrQPBPfZ2KRKk5X.png'

const STARS_ICON = '✦'

function formatGeneratedContent(content: string | string[]): string | string[] {
  const clean = (s: string) =>
    s
      .replace(/^```[\w]*\n?/, '')
      .replace(/\n?```$/, '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/^["'[,]+|["'\],]+$/g, '')
      .trim()
  if (Array.isArray(content)) return content.map(clean).filter(Boolean)
  return clean(content)
}

const CarWithDriverStep4: React.FC<Props> = ({
  data,
  onDataChange,
  currentStep,
  totalSteps,
  maxStep,
  draftId,
  onExit,
  onBack,
  onNext,
  onPublish,
  onFinish,
}) => {
  const { userLoggedIn } = useAuth()
  const { t } = useCwdLang()
  const [view, setView] = useState<View>('service-info')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  // AI generation state
  const [generatingHighlights, setGeneratingHighlights] = useState(false)
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [prevHighlights, setPrevHighlights] = useState<string[] | null>(null)
  const [prevDescription, setPrevDescription] = useState<string | null>(null)

  const [serviceInfoError, setServiceInfoError] = useState('')
  const [policiesError, setPoliciesError] = useState('')

  const apiUrl = process.env.REACT_APP_TS_RESTFUL_API_URL

  // ── Highlight helpers ────────────────────────────────────────────────
  const updateHighlight = (idx: number, val: string) => {
    const updated = [...data.highlights]
    updated[idx] = val
    onDataChange({ ...data, highlights: updated })
  }

  const addHighlight = () => {
    onDataChange({ ...data, highlights: [...data.highlights, ''] })
  }

  const removeHighlight = (idx: number) => {
    const updated = data.highlights.filter((_, i) => i !== idx)
    onDataChange({ ...data, highlights: updated.length > 0 ? updated : [''] })
  }

  // ── Included/not-included helpers ────────────────────────────────────
  const updateIncluded = (idx: number, val: string) => {
    const updated = [...data.included]
    updated[idx] = val
    onDataChange({ ...data, included: updated })
  }

  const addIncluded = () => onDataChange({ ...data, included: [...data.included, ''] })

  const removeIncluded = (idx: number) => {
    const updated = data.included.filter((_, i) => i !== idx)
    onDataChange({ ...data, included: updated.length > 0 ? updated : [''] })
  }

  const updateNotIncluded = (idx: number, val: string) => {
    const updated = [...data.notIncluded]
    updated[idx] = val
    onDataChange({ ...data, notIncluded: updated })
  }

  const addNotIncluded = () => onDataChange({ ...data, notIncluded: [...data.notIncluded, ''] })

  const removeNotIncluded = (idx: number) => {
    const updated = data.notIncluded.filter((_, i) => i !== idx)
    onDataChange({ ...data, notIncluded: updated.length > 0 ? updated : [''] })
  }

  // ── AI Generation ────────────────────────────────────────────────────
  const handleGenerateHighlights = async () => {
    if (!userLoggedIn?.userToken) return
    setPrevHighlights(data.highlights)
    setGeneratingHighlights(true)
    try {
      const res = await fetch(
        `${apiUrl}/car-with-driver/submission/${draftId ?? 0}/generate-content`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: JSON.stringify({ type: 'highlights' }),
        },
      )
      const json = await res.json()
      if (json.result && json.data?.highlights) {
        const formatted = formatGeneratedContent(json.data.highlights) as string[]
        onDataChange({ ...data, highlights: formatted })
      }
    } catch {
      // silently fail, user can try again
    } finally {
      setGeneratingHighlights(false)
    }
  }

  const handleGenerateDescription = async () => {
    if (!userLoggedIn?.userToken) return
    setPrevDescription(data.description)
    setGeneratingDescription(true)
    try {
      const res = await fetch(
        `${apiUrl}/car-with-driver/submission/${draftId ?? 0}/generate-content`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: JSON.stringify({ type: 'description' }),
        },
      )
      const json = await res.json()
      if (json.result && json.data?.description) {
        const formatted = formatGeneratedContent(json.data.description) as string
        onDataChange({ ...data, description: formatted })
      }
    } catch {
      // silently fail
    } finally {
      setGeneratingDescription(false)
    }
  }

  // ── Navigation ───────────────────────────────────────────────────────
  const handleServiceInfoNext = () => {
    const validHighlights = data.highlights.filter((h) => h.trim())
    if (validHighlights.length === 0) {
      setServiceInfoError(t.step4.errHighlight)
      return
    }
    setServiceInfoError('')
    setView('policies')
  }

  const handlePoliciesNext = async () => {
    if (!data.overtimeFee) {
      setPoliciesError(t.step4.errOvertimeFee)
      return
    }
    setPoliciesError('')
    setIsSubmitting(true)
    await onNext(data, false)
    setIsSubmitting(false)
    setView('complete')
  }

  // ── Step progress ─────────────────────────────────────────────────────
  const stepProgress =
    view === 'complete'
      ? 100
      : view === 'policies'
      ? 75
      : data.highlights.some((h) => h.trim()) || data.description.trim()
      ? 50
      : 0

  // ── Reusable styles ───────────────────────────────────────────────────
  const sectionTitle = (text: string, required = false) => (
    <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 2px' }}>
      {text}
      {required && <RequiredMark />}
    </p>
  )

  const sectionDesc = (text: string) => (
    <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 12px' }}>{text}</p>
  )

  const divider = <div style={{ height: '1px', backgroundColor: '#F0F0F0', margin: '20px 0' }} />

  const aiRow = (generating: boolean, onGenerate: () => void, prevVal: any, onUndo: () => void) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
      }}
    >
      <button
        onClick={onGenerate}
        disabled={generating}
        style={{
          background: 'none',
          border: 'none',
          cursor: generating ? 'not-allowed' : 'pointer',
          color: generating ? '#99afd4' : '#0048B3',
          fontSize: '13px',
          fontWeight: '600',
          fontFamily: 'Poppins, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          padding: 0,
        }}
      >
        <span style={{ fontSize: '14px' }}>{STARS_ICON}</span>
        {generating ? t.step4.generating : t.step4.generateWithAi}
      </button>
      {prevVal !== null && (
        <button
          onClick={onUndo}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#667085',
            fontSize: '13px',
            fontFamily: 'Poppins, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
          }}
        >
          {t.step4.undo}
        </button>
      )}
    </div>
  )

  const dropdownField = (
    label: string,
    desc: string,
    value: string,
    options: string[],
    onChange: (v: string) => void,
  ) => (
    <div style={{ marginBottom: '20px' }}>
      <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 2px' }}>
        {label}
      </p>
      <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 10px' }}>{desc}</p>
      <div style={{ position: 'relative' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 36px 12px 14px',
            border: '1px solid #D6DAE0',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif',
            appearance: 'none' as any,
            WebkitAppearance: 'none' as any,
            backgroundColor: '#fff',
            color: '#212427',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {t.step4.optionLabels[o] ?? o}
            </option>
          ))}
        </select>
        <span
          style={{
            position: 'absolute',
            right: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: '#667085',
            fontSize: '12px',
          }}
        >
          ▾
        </span>
      </div>
    </div>
  )

  const feeField = (
    label: string,
    desc: string,
    value: string,
    onChange: (v: string) => void,
    required = false,
  ) => (
    <div style={{ marginBottom: '20px' }}>
      <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 2px' }}>
        {label}
        {required && <RequiredMark />}
      </p>
      <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 10px' }}>{desc}</p>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          border: '1px solid #D6DAE0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <input
          type="number"
          placeholder="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onWheel={(e) => e.currentTarget.blur()}
          style={{
            flex: 1,
            padding: '12px 12px',
            border: 'none',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif',
            outline: 'none',
            minWidth: 0,
          }}
        />
        <span
          style={{
            padding: '0 14px',
            fontSize: '13px',
            color: '#667085',
            flexShrink: 0,
            borderLeft: '1px solid #F0F0F0',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FAFAFA',
          }}
        >
          THB
        </span>
      </div>
    </div>
  )

  // ── Service Info View ─────────────────────────────────────────────────
  if (view === 'service-info') {
    return (
      <div style={pageStyle}>
        <TopBar onExit={onExit} />
        <ProgressHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          maxStep={maxStep}
          title={t.step4.serviceInfoTitle}
          currentStepProgress={stepProgress}
        />
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {sectionTitle(t.step4.highlightsTitle, true)}
          {sectionDesc(t.step4.highlightsDesc)}

          {aiRow(generatingHighlights, handleGenerateHighlights, prevHighlights, () => {
            onDataChange({ ...data, highlights: prevHighlights! })
            setPrevHighlights(null)
          })}

          {data.highlights.map((h, idx) => (
            <div key={idx} style={{ marginBottom: '12px' }}>
              <div style={{ position: 'relative' }}>
                <textarea
                  value={h}
                  onChange={(e) => updateHighlight(idx, e.target.value)}
                  maxLength={250}
                  rows={2}
                  placeholder={t.step4.highlightPlaceholder}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #D6DAE0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif',
                    outline: 'none',
                    resize: 'none',
                    boxSizing: 'border-box',
                    paddingRight: data.highlights.length > 1 ? '44px' : '12px',
                  }}
                />
                {data.highlights.length > 1 && (
                  <button
                    onClick={() => removeHighlight(idx)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '8px',
                      width: '30px',
                      height: '30px',
                      border: '1px solid #FECACA',
                      borderRadius: '6px',
                      backgroundColor: '#FEF2F2',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      color: '#ef4444',
                    }}
                  >
                    <DeleteOutlined />
                  </button>
                )}
              </div>
              <p
                style={{
                  fontSize: '12px',
                  color: '#667085',
                  textAlign: 'right',
                  margin: '4px 0 0',
                }}
              >
                {t.common.charCount(h.length, 250)}
              </p>
            </div>
          ))}

          <button onClick={addHighlight} style={addMoreBtnStyle}>
            {t.step4.addHighlight}
          </button>

          {divider}

          {sectionTitle(t.step4.descriptionTitle)}

          {aiRow(generatingDescription, handleGenerateDescription, prevDescription, () => {
            onDataChange({ ...data, description: prevDescription! })
            setPrevDescription(null)
          })}

          <textarea
            value={data.description}
            onChange={(e) => onDataChange({ ...data, description: e.target.value })}
            maxLength={500}
            rows={5}
            placeholder={t.step4.descriptionPlaceholder}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #D6DAE0',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              outline: 'none',
              resize: 'none',
              boxSizing: 'border-box',
            }}
          />
          <p
            style={{
              fontSize: '12px',
              color: data.description.length >= 450 ? '#0048B3' : '#667085',
              textAlign: 'right',
              margin: '4px 0 16px',
            }}
          >
            {t.common.charCount(data.description.length, 500)}
          </p>

          {serviceInfoError && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 8px' }}>
              {serviceInfoError}
            </p>
          )}
        </div>
        <BottomNav
          onBack={onBack}
          onNext={handleServiceInfoNext}
          nextDisabled={data.highlights.filter((h) => h.trim()).length === 0}
        />
      </div>
    )
  }

  // ── Policies View ─────────────────────────────────────────────────────
  if (view === 'policies') {
    return (
      <div style={pageStyle}>
        <TopBar onExit={onExit} />
        <ProgressHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          maxStep={maxStep}
          title={t.step4.policiesTitle}
          currentStepProgress={stepProgress}
        />
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {/* Instant booking */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: 0 }}>
                {t.step4.instantBooking}
              </p>
              <span
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  border: '1px solid #667085',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: '#667085',
                  cursor: 'default',
                }}
              >
                i
              </span>
            </div>
            <div
              onClick={() => onDataChange({ ...data, instantBooking: !data.instantBooking })}
              style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: data.instantBooking ? '#0048B3' : '#D6DAE0',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: '3px',
                  left: data.instantBooking ? '23px' : '3px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </div>
          </div>

          {divider}

          {dropdownField(
            t.step4.freeWaitingTime,
            t.step4.freeWaitingTimeDesc,
            data.freeWaitingTime,
            FREE_WAITING_OPTIONS,
            (v) => onDataChange({ ...data, freeWaitingTime: v }),
          )}

          {feeField(
            t.step4.overtimeFee,
            t.step4.overtimeFeeDesc,
            data.overtimeFee,
            (v) => onDataChange({ ...data, overtimeFee: v }),
            true,
          )}

          {feeField(t.step4.babySeatFee, t.step4.babySeatFeeDesc, data.extraBabySeatFee, (v) =>
            onDataChange({ ...data, extraBabySeatFee: v }),
          )}

          {divider}

          {dropdownField(
            t.step4.cancellationPolicy,
            t.step4.cancellationPolicyDesc,
            data.cancellationPolicy,
            CANCELLATION_OPTIONS,
            (v) => onDataChange({ ...data, cancellationPolicy: v }),
          )}

          {dropdownField(
            t.step4.advancedBookingTime,
            t.step4.advancedBookingTimeDesc,
            data.advancedBookingTime,
            ADVANCED_BOOKING_OPTIONS,
            (v) => onDataChange({ ...data, advancedBookingTime: v }),
          )}

          {divider}

          {/* What's included */}
          {sectionTitle(t.step4.includedTitle)}
          {sectionDesc(t.step4.includedDesc)}
          {data.included.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}
              >
                <input
                  type="text"
                  value={item}
                  onChange={(e) => updateIncluded(idx, e.target.value)}
                  maxLength={250}
                  placeholder={t.step4.includedPlaceholder}
                  style={textInputStyle}
                />
                {data.included.length > 1 && (
                  <button onClick={() => removeIncluded(idx)} style={deleteIconBtnStyle}>
                    <DeleteOutlined />
                  </button>
                )}
              </div>
              <p
                style={{
                  fontSize: '11px',
                  color: '#667085',
                  textAlign: 'right',
                  margin: '0 0 4px',
                }}
              >
                {t.common.charCount(item.length, 250)}
              </p>
            </div>
          ))}
          <button onClick={addIncluded} style={addMoreBtnStyle}>
            {t.step4.addItem}
          </button>

          {divider}

          {/* What's not included */}
          {sectionTitle(t.step4.notIncludedTitle)}
          {sectionDesc(t.step4.notIncludedDesc)}
          {data.notIncluded.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}
              >
                <input
                  type="text"
                  value={item}
                  onChange={(e) => updateNotIncluded(idx, e.target.value)}
                  maxLength={250}
                  placeholder={t.step4.notIncludedPlaceholder}
                  style={textInputStyle}
                />
                {data.notIncluded.length > 1 && (
                  <button onClick={() => removeNotIncluded(idx)} style={deleteIconBtnStyle}>
                    <DeleteOutlined />
                  </button>
                )}
              </div>
              <p
                style={{
                  fontSize: '11px',
                  color: '#667085',
                  textAlign: 'right',
                  margin: '0 0 4px',
                }}
              >
                {t.common.charCount(item.length, 250)}
              </p>
            </div>
          ))}
          <button onClick={addNotIncluded} style={addMoreBtnStyle}>
            {t.step4.addItem}
          </button>

          {policiesError && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '12px 0 0' }}>
              {policiesError}
            </p>
          )}
          <div style={{ height: '16px' }} />
        </div>
        <BottomNav
          onBack={() => setView('service-info')}
          onNext={handlePoliciesNext}
          nextDisabled={!data.overtimeFee.trim()}
          loading={isSubmitting}
        />
      </div>
    )
  }

  // ── Loading View ──────────────────────────────────────────────────────
  if (view === 'loading') {
    return (
      <div
        style={{
          ...pageStyle,
          alignItems: 'center',
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <style>{`
          @keyframes cwd-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes cwd-pulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.4; }
          }
        `}</style>
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: '5px solid #E0F7F7',
            borderTopColor: '#00CCCC',
            animation: 'cwd-spin 0.9s linear infinite',
          }}
        />
        <p
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#212427',
            margin: 0,
            animation: 'cwd-pulse 1.4s ease-in-out infinite',
          }}
        >
          {t.step4.publishing}
        </p>
      </div>
    )
  }

  // ── Success View ──────────────────────────────────────────────────────
  if (view === 'success') {
    const lineIcon =
      'https://ts-upload.sgp1.digitaloceanspaces.com/cms/17UmBKpMFpFyWcD2bkWqogewV3BahiXQ2ekiwzJvUDgA1gee6K3mQDaGMQGULEVoHJYpSTrPb57KaszybWfA6Loo.png'
    const lineUrl = 'https://line.me/R/ti/p/@haup.partner'
    const remainingSteps = [
      { label: t.common.applicationSubmitted, done: true, desc: '' },
      { label: t.common.carListed, done: true, desc: '' },
      {
        label: t.common.uploadRequiredDocs,
        done: true,
        desc: t.common.uploadRequiredDocsDesc,
      },
      {
        label: t.common.downloadApp,
        done: false,
        desc: t.common.downloadAppDesc,
      },
    ]
    return (
      <div style={pageStyle}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {/* Success icon + title */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#00CCCC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
              }}
            >
              <span style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>✓</span>
            </div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#212427',
                margin: '0 0 4px',
                lineHeight: '1.3',
              }}
            >
              {t.common.successTitle}
            </h2>
          </div>

          {/* LINE connect */}
          <div
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 8px' }}>
              {t.common.connectLineTitle}
            </p>
            <p
              style={{ fontSize: '13px', color: '#667085', margin: '0 0 12px', lineHeight: '1.5' }}
            >
              {t.common.connectLineDesc}
            </p>
            <div
              onClick={() => window.open(lineUrl, '_blank', 'noopener,noreferrer')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: '1px solid #D6DAE0',
                borderRadius: '8px',
                padding: '10px 14px',
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              <img src={lineIcon} alt="LINE" style={{ width: '24px', height: '24px' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#212427' }}>
                @haup.partner
              </span>
            </div>
          </div>

          {/* Remaining steps */}
          <div
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <p
              style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 16px' }}
            >
              {t.common.remainingSteps}
            </p>
            {remainingSteps.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: i < remainingSteps.length - 1 ? '12px' : 0,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: s.done ? '#00CCCC' : '#fff',
                      border: s.done ? 'none' : '2px solid #D6DAE0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {s.done && (
                      <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>
                    )}
                  </div>
                  {i < remainingSteps.length - 1 && (
                    <div
                      style={{ width: '2px', flex: 1, backgroundColor: '#D6DAE0', margin: '4px 0' }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, paddingBottom: i < remainingSteps.length - 1 ? '8px' : 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: s.desc ? '4px' : 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: s.done ? '600' : '400',
                        color: '#212427',
                      }}
                    >
                      {s.label}
                    </span>
                    {!s.done && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#F59E0B',
                          backgroundColor: '#FEF3C7',
                          padding: '2px 8px',
                          borderRadius: '10px',
                        }}
                      >
                        {t.common.notDone}
                      </span>
                    )}
                  </div>
                  {s.desc && (
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#667085',
                        margin: '0 0 8px',
                        lineHeight: '1.4',
                      }}
                    >
                      {s.desc}
                    </p>
                  )}
                  {!s.done && (
                    <button
                      onClick={() =>
                        window.open('https://haupcar.com/download', '_blank', 'noopener,noreferrer')
                      }
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #D6DAE0',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#333',
                      }}
                    >
                      {t.common.downloadApp}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manage button */}
        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          <button
            onClick={onFinish}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#0048B3',
              color: '#fff',
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            {t.common.manageYourCars}
          </button>
        </div>
      </div>
    )
  }

  // ── Complete View ─────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px' }}>
        {/* Main steps completed card */}
        <div
          style={{
            backgroundColor: '#F0F9FF',
            borderRadius: '16px',
            padding: '24px 16px',
            textAlign: 'center',
            marginBottom: '24px',
          }}
        >
          <img
            src={SUCCESS_ICON}
            alt="success"
            style={{ width: '140px', height: '140px', objectFit: 'contain', marginBottom: '12px' }}
          />
          <h2
            style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#0048B3',
              margin: '0 0 8px',
              lineHeight: '1.3',
            }}
          >
            {t.step4.completeTitle}
          </h2>
          <p style={{ fontSize: '14px', color: '#667085', margin: '0 0 20px', lineHeight: '1.5' }}>
            {t.step4.completeDesc}
          </p>
          {t.step4.completedSteps.map((label, i, arr) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: i < arr.length - 1 ? '10px' : 0,
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: '#00CCCC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>
              </div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#212427', margin: 0 }}>
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Optional step section */}
        <p style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 8px' }}>
          {t.step4.optionalTitle}
        </p>
        <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 16px', lineHeight: '1.5' }}>
          {t.step4.optionalDesc}
        </p>

        {/* Step 5 card */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 6px' }}>
              5.&nbsp; {t.step4.uploadDocsStep}
            </p>
            <p style={{ fontSize: '13px', color: '#667085', margin: 0, lineHeight: '1.5' }}>
              {t.step4.uploadDocsStepDesc}
            </p>
          </div>
          <img
            src={UPLOAD_ICON}
            alt="upload"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              flexShrink: 0,
              borderRadius: '8px',
            }}
          />
        </div>
      </div>

      {/* Bottom buttons */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          padding: '16px',
          borderTop: '1px solid #f0f0f0',
          flexShrink: 0,
        }}
      >
        <button
          disabled={isPublishing}
          onClick={async () => {
            setIsPublishing(true)
            setView('loading')
            try {
              await Promise.all([onPublish(), new Promise((resolve) => setTimeout(resolve, 2000))])
              setView('success')
            } catch {
              setView('complete')
            } finally {
              setIsPublishing(false)
            }
          }}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: '#fff',
            color: isPublishing ? '#99afd4' : '#0048B3',
            border: `1.5px solid ${isPublishing ? '#99afd4' : '#0048B3'}`,
            borderRadius: '24px',
            cursor: isPublishing ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '15px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {t.common.skipAndSubmit}
        </button>
        <button
          disabled={isPublishing}
          onClick={async () => {
            setIsPublishing(true)
            try {
              await onPublish()
              await onNext(data)
            } finally {
              setIsPublishing(false)
            }
          }}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: isPublishing ? '#99afd4' : '#0048B3',
            color: '#fff',
            border: 'none',
            borderRadius: '24px',
            cursor: isPublishing ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '15px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {isPublishing ? t.common.submitting : t.step4.continue}
        </button>
      </div>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  backgroundColor: '#fff',
  minHeight: '100vh',
  maxWidth: '500px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}

const addMoreBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0048B3',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  padding: '4px 0 8px',
  fontFamily: 'Poppins, sans-serif',
}

const textInputStyle: React.CSSProperties = {
  flex: 1,
  padding: '11px 14px',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
}

const deleteIconBtnStyle: React.CSSProperties = {
  width: '38px',
  height: '38px',
  border: '1px solid #FECACA',
  borderRadius: '8px',
  backgroundColor: '#FEF2F2',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  color: '#ef4444',
  flexShrink: 0,
}

export default CarWithDriverStep4
